import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AuthProvider, useAuth, AuthModal } from 'shared-auth';
import { NotificationProvider, useNotification } from './components/NotificationSystem';
import { ConfigProvider, useConfig } from 'shared-config';
import SpeedTestUI from './components/PromptInput';
import HistoryPanel from './components/HistoryPanel';
import AIAnalysisModal from './components/AIAnalysisModal';
import HistoryChartModal from './components/HistoryChartModal';
import ThemeToggle from './components/ThemeToggle';
import LinkRestoreModal from './components/LinkRestoreModal';
import FeedbackModal from './components/FeedbackModal';
import RatingsPage from './components/RatingsPage';
import PwaInstructionsModal from './components/PwaInstructionsModal';
import PwaInstallPrompt from './components/PwaInstallPrompt';
import InfoPageModal from './components/InfoPageModal';
import ResultPage from './components/ResultPage';
import CustomServerModal from './components/CustomServerModal';
import SearchKeywords from './components/SearchKeywords';
import Recommendations from './components/Recommendations';
import SettingsModal from './components/SettingsModal';
import PushNotificationPrompt from './components/PushNotificationPrompt';
import AuthWidget from './components/AuthWidget';
import { Server } from 'shared-types';
import {
  TestState,
  Theme,
  SpeedTestResult,
  HistoricalTestResult,
  ConnectionInfo,
  SharedTestResult,
  LegacySharedTestResult,
  CompactSharedTestResult,
  AIAnalysisResult,
  RecommendationsResult,
  LinkQuality,
  TestProtocol,
  TestMode,
  StreamingAnalysis,
} from './types';
// Fix: Import `fetchConnectionInfo` to resolve the 'Cannot find name' error.
import { runSpeedTest as runSpeedTestOverHttp, pingServer, validateAndCreateCustomServer, fetchConnectionInfo, getUserLocation, calculateDistance } from './services/networkInfoService';
import { runSpeedTestOverWebSocket } from './services/websocketService';
import { analyzeSpeedTestResults, getRecommendations, analyzeStreamingQuality } from './services/geminiService';
import { calculateLinkQuality, getIncompleteTestQuality } from 'shared-grading';
import { ClipboardIcon, CheckIcon, ShieldCheckIcon, SettingsIcon } from 'shared-icons';

// Base URL for API calls, determined by environment variable and sanitized
const BASE_URL = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, '');

const AppContainer: React.FC = () => {
    return (
        <ConfigProvider siteId="speedtest" backendUrl={BASE_URL}>
            <AuthProvider>
                <NotificationProvider>
                    <App />
                    <AuthModal />
                </NotificationProvider>
            </AuthProvider>
        </ConfigProvider>
    )
}

const parseUserAgent = (): string => {
    const ua = navigator.userAgent;
    let browser = 'Unknown Browser';
    let os = 'Unknown OS';

    // OS Detection
    if (/Windows/i.test(ua)) os = 'Windows';
    else if (/Macintosh|Mac OS X/i.test(ua)) os = 'macOS';
    else if (/Android/i.test(ua)) os = 'Android';
    else if (/iPhone|iPad|iPod/i.test(ua)) os = 'iOS';
    else if (/Linux/i.test(ua)) os = 'Linux';

    // Browser Detection (order is important)
    if (/Edg/i.test(ua)) browser = 'Edge';
    else if (/Chrome/i.test(ua) && !/Chromium/i.test(ua)) browser = 'Chrome';
    else if (/Firefox/i.test(ua)) browser = 'Firefox';
    else if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) browser = 'Safari';
    
    return `${browser} on ${os}`;
}

const App: React.FC = () => {
  const { config, loading: configLoading, error: configError } = useConfig();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const notify = useNotification();
  
  const [theme, setTheme] = useState<Theme>('auto');
  const [testProtocol, setTestProtocol] = useState<TestProtocol>('http');
  const [testMode, setTestMode] = useState<TestMode>('speed');
  const [testState, setTestState] = useState<TestState>('idle');
  const [lastCompletedMode, setLastCompletedMode] = useState<TestMode | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [results, setResults] = useState<SpeedTestResult>({ ping: 0, jitter: 0, download: 0, upload: 0 });
  const [currentSpeed, setCurrentSpeed] = useState(0);
  const [pingHistory, setPingHistory] = useState<number[]>([]);
  const [jitterHistory, setJitterHistory] = useState<number[]>([]);
  const [linkQuality, setLinkQuality] = useState<LinkQuality | null>(null);
  
  const [connectionInfo, setConnectionInfo] = useState<ConnectionInfo | null>(null);
  const [servers, setServers] = useState<Server[]>([]);
  const [selectedServer, setSelectedServer] = useState<Server | null>(null);
  const [isFindingBestServer, setIsFindingBestServer] = useState(true);
  const [manualServerSelection, setManualServerSelection] = useState(false);
  
  const [history, setHistory] = useState<HistoricalTestResult[]>([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [connectionMode, setConnectionMode] = useState<'single' | 'multi'>('multi');

  const [isHistoryChartModalOpen, setHistoryChartModalOpen] = useState(false);
  const [isLinkRestoreModalOpen, setLinkRestoreModalOpen] = useState(false);
  const [isFeedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [isRatingsPageOpen, setRatingsPageOpen] = useState(false);
  const [isPwaModalOpen, setPwaModalOpen] = useState(false);
  const [isAIAnalysisModalOpen, setAIAnalysisModalOpen] = useState(false);
  const [isCustomServerModalOpen, setCustomServerModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [infoModalData, setInfoModalData] = useState<{ title: string; content: React.ReactNode } | null>(null);
  
  const [analysis, setAnalysis] = useState<AIAnalysisResult | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);

  const [streamingAnalysis, setStreamingAnalysis] = useState<StreamingAnalysis[] | null>(null);
  const [isAnalyzingStreaming, setIsAnalyzingStreaming] = useState(false);
  const [streamingError, setStreamingError] = useState<string | null>(null);

  const [embedCopied, setEmbedCopied] = useState(false);
  
  const [recommendations, setRecommendations] = useState<RecommendationsResult | null>(null);
  const [isFetchingRecs, setIsFetchingRecs] = useState(false);
  const [recsError, setRecsError] = useState<string | null>(null);

  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const platform = useRef<'ios' | 'android' | 'other_desktop' | null>(null);

  const [sharedResult, setSharedResult] = useState<SharedTestResult | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const prevIsOnline = useRef(navigator.onLine);
  const wasTestInterruptedByOffline = useRef(false);

  const onOpenCustomServerModal = () => setCustomServerModalOpen(true);
  const onOpenFeedbackModal = () => {
      if (history.length > 0) {
          setFeedbackModalOpen(true);
      } else {
          notify({ type: 'error', title: 'No Test Data', message: 'Please run a test before leaving feedback.' });
      }
  };

  // Load servers from config
  useEffect(() => {
    if (config?.testConfig?.servers) {
      // Load custom servers from localStorage and merge them
      try {
          const customServersJson = localStorage.getItem('custom-servers');
          const customServers = customServersJson ? JSON.parse(customServersJson) : [];
          const allServers = [...config.testConfig.servers, ...customServers];
          setServers(allServers);
          setSelectedServer(allServers[0]);
      } catch(e) {
          console.error("Failed to load custom servers:", e);
          setServers(config.testConfig.servers);
          setSelectedServer(config.testConfig.servers[0]);
      }
    }
  }, [config]);


  // PWA & initial data loading
  useEffect(() => {
    // Load saved test protocol preference
    const savedProtocol = localStorage.getItem('testProtocol') as TestProtocol;
    if (savedProtocol && ['http', 'websocket'].includes(savedProtocol)) {
        setTestProtocol(savedProtocol);
    }
    
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js')
          .then(reg => console.log('SW registered!', reg))
          .catch(err => console.log('SW registration failed: ', err));
      });
    }

    const urlParams = new URLSearchParams(window.location.search);
    const resultData = urlParams.get('result');
    if (resultData) {
      try {
        const decodedString = atob(resultData);
        const parsedJson = JSON.parse(decodedString);
        let normalizedResult: SharedTestResult | null = null;
        if (parsedJson && (parsedJson.v === 1 || parsedJson.v === undefined) && parsedJson.results) {
          const legacyResult = parsedJson as LegacySharedTestResult;
          normalizedResult = {
            date: legacyResult.date,
            results: legacyResult.results,
            connectionInfo: { isp: legacyResult.connectionInfo.isp, serverLocation: legacyResult.connectionInfo.serverLocation },
            server: legacyResult.server,
          };
        } else if (parsedJson && parsedJson.v === 2 && parsedJson.r) {
          const compactResult = parsedJson as CompactSharedTestResult;
          normalizedResult = {
            date: compactResult.dt,
            results: { download: compactResult.r.d, upload: compactResult.r.u, ping: compactResult.r.p, jitter: compactResult.r.j },
            connectionInfo: { isp: compactResult.c.i, serverLocation: compactResult.c.l },
            server: { name: compactResult.s.n, location: compactResult.s.l },
          };
        }
        if (normalizedResult) { setSharedResult(normalizedResult); window.history.replaceState(null, '', window.location.pathname); }
        else { throw new Error("Invalid or unsupported shared result format."); }
      } catch (e) { console.error("Failed to parse shared result:", e); window.history.replaceState(null, '', window.location.pathname); }
    }

    const savedTheme = localStorage.getItem('theme') as Theme | null;
    setTheme(savedTheme || 'auto');
    const handleBeforeInstallPrompt = (e: Event) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    const userAgent = window.navigator.userAgent;
    if (/android/i.test(userAgent)) platform.current = 'android';
    else if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) platform.current = 'ios';
    else platform.current = 'other_desktop';
    
    return () => { window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt); };
  }, []);
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
        if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
            setIsMenuOpen(false);
        }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuRef]);

  useEffect(() => {
    if (sharedResult) return;
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone;
    const wasDismissed = localStorage.getItem('pwaInstallPromptDismissed') === 'true';
    const hasCompletedFirstTest = localStorage.getItem('hasCompletedFirstTest') === 'true';
    if (hasCompletedFirstTest && !isStandalone && !wasDismissed) {
        if (platform.current === 'ios' || deferredPrompt) {
            setShowInstallPrompt(true);
        }
    }
  }, [deferredPrompt, sharedResult, testState]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const applyTheme = () => {
        const isDark = theme === 'auto' ? mediaQuery.matches : theme === 'dark';
        document.documentElement.classList.toggle('dark', isDark);
    };
    applyTheme();
    if (theme === 'auto') localStorage.removeItem('theme');
    else localStorage.setItem('theme', theme);
    mediaQuery.addEventListener('change', applyTheme);
    return () => mediaQuery.removeEventListener('change', applyTheme);
  }, [theme]);

  const cycleTheme = () => {
    const sequence: Theme[] = ['light', 'dark', 'auto'];
    const currentIndex = sequence.findIndex(t => t === theme);
    setTheme(sequence[(currentIndex + 1) % sequence.length]);
  };

  useEffect(() => {
    if (!prevIsOnline.current && isOnline) {
      if (wasTestInterruptedByOffline.current) setLinkRestoreModalOpen(true);
    }
    prevIsOnline.current = isOnline;
  }, [isOnline]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  useEffect(() => {
    const isAnyModalOpen = isAIAnalysisModalOpen || isHistoryChartModalOpen || isLinkRestoreModalOpen || 
                           isFeedbackModalOpen || isRatingsPageOpen || isPwaModalOpen || isCustomServerModalOpen || isSettingsModalOpen || !!infoModalData;
    document.body.style.overflow = isAnyModalOpen ? 'hidden' : 'auto';
    return () => { document.body.style.overflow = 'auto'; };
}, [isAIAnalysisModalOpen, isHistoryChartModalOpen, isLinkRestoreModalOpen, isFeedbackModalOpen, isRatingsPageOpen, isPwaModalOpen, isCustomServerModalOpen, isSettingsModalOpen, infoModalData]);

  // Load history based on authentication state
  useEffect(() => {
    const loadHistory = async () => {
        if (authLoading) return; // Wait for auth state to be resolved

        if (isAuthenticated) {
            try {
                const response = await fetch(`${BASE_URL}/api/users/me/history`, {
                    credentials: 'include' // Send cookies
                });
                if (!response.ok) throw new Error('Failed to fetch user history');
                const data = await response.json();
                // Robustness: Gracefully handle non-array responses instead of throwing an error.
                if (Array.isArray(data)) {
                    setHistory(data);
                } else {
                    console.warn("Received non-array response for history, treating as empty.", data);
                    setHistory([]);
                }
            } catch (e) {
                console.error("Failed to fetch user history from backend", e);
                setHistory([]); // Set to empty array on failure
                notify({ type: 'error', title: 'Could Not Load History', message: 'Failed to retrieve your test history from the server.' });
            }
        } else {
            try {
                const savedHistory = localStorage.getItem('speedtest-history');
                setHistory(savedHistory ? JSON.parse(savedHistory) : []);
            } catch (e) {
                console.error("Failed to load history from localStorage", e);
                setHistory([]);
            }
        }
    };
    loadHistory();
  }, [isAuthenticated, authLoading, notify]);

  // Merge local history into account on first login of a session
  const hasMerged = useRef(false);
  useEffect(() => {
    const mergeLocalHistory = async () => {
        if (isAuthenticated && !hasMerged.current) {
            hasMerged.current = true; // Prevent running this more than once per session
            const localHistoryJson = localStorage.getItem('speedtest-history');
            if (localHistoryJson) {
                const localHistory = JSON.parse(localHistoryJson);
                if (localHistory && localHistory.length > 0) {
                    try {
                        const response = await fetch(`${BASE_URL}/api/history/merge`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(localHistory),
                            credentials: 'include'
                        });
                        if (response.ok) {
                            localStorage.removeItem('speedtest-history');
                            // Refetch the full history to include the merged results
                            const historyResponse = await fetch(`${BASE_URL}/api/users/me/history`, {
                                credentials: 'include'
                            });
                            const data = await historyResponse.json();
                            setHistory(data);
                            notify({ type: 'success', title: 'History Synced', message: 'Your local test history has been saved to your account.' });
                        }
                    } catch (e) {
                        console.error("Failed to merge local history:", e);
                    }
                }
            }
        }
    };
    if (!authLoading) {
        mergeLocalHistory();
    }
  }, [isAuthenticated, authLoading, notify]);

  const clearHistory = () => {
    setHistory([]);
    if (!isAuthenticated) {
        localStorage.removeItem('speedtest-history');
    }
    notify({
      type: 'info',
      title: 'History Cleared',
      message: isAuthenticated
        ? 'Your history has been cleared from this view. It will reappear on your next visit.'
        : 'Your local test history has been cleared.'
    });
  };

  useEffect(() => {
    if (sharedResult || servers.length === 0) return;
    const initialize = async () => {
      // Fetch connection info and user location in parallel for efficiency
      const [info, userLocation] = await Promise.all([
        fetchConnectionInfo(),
        getUserLocation(),
      ]);
      setConnectionInfo(info);
      
      setIsFindingBestServer(true);
      
      // Map servers to include ping and distance calculations
      const serverPromises = servers.map(async server => {
        const ping = await pingServer(server);
        const distance = (userLocation && server.lat && server.lon)
          ? calculateDistance(userLocation.latitude, userLocation.longitude, server.lat, server.lon)
          : undefined;
        return { ...server, ping, distance };
      });
      
      const serversWithDetails = await Promise.all(serverPromises);

      // The best server is still the one with the lowest ping
      const sortedServers = [...serversWithDetails].sort((a, b) => (a.ping ?? 9999) - (b.ping ?? 9999));
      setServers(sortedServers);
      if (!manualServerSelection) setSelectedServer(sortedServers[0]);
      setIsFindingBestServer(false);
    };
    initialize();
  }, [sharedResult, servers.length]);

  const fetchRecommendations = async (currentResults: SpeedTestResult) => {
    setIsFetchingRecs(true); setRecsError(null); setRecommendations(null);
    try { setRecommendations(await getRecommendations(currentResults)); }
    catch (e) { setRecsError(e instanceof Error ? e.message : "An unknown error occurred while fetching recommendations."); }
    finally { setIsFetchingRecs(false); }
  };

  const startTest = () => {
    if ((testState !== 'idle' && testState !== 'finished' && testState !== 'error') || !selectedServer) return;
    setTestState('idle'); setTestError(null); setResults({ ping: 0, jitter: 0, download: 0, upload: 0 });
    setCurrentSpeed(0); setPingHistory([]); setJitterHistory([]); setAnalysis(null); setAnalysisError(null);
    setStreamingAnalysis(null); setStreamingError(null); setIsAnalyzingStreaming(false);
    setLinkQuality(null); wasTestInterruptedByOffline.current = false; setRecommendations(null); setRecsError(null); setIsFetchingRecs(false);
    setLastCompletedMode(null);

    const testCallbacks = {
      onStateChange: (state: TestState) => setTestState(state),
      onPingUpdate: (ping: number, jitter: number) => {
          setResults(prev => ({...(prev || { ping: 0, jitter: 0, download: 0, upload: 0 }), ping, jitter}));
          setPingHistory(prev => [...prev.slice(-20), ping]);
          setJitterHistory(prev => [...prev.slice(-20), jitter]);
      },
      onDownloadUpdate: (speed: number) => setCurrentSpeed(speed),
      onUploadUpdate: (speed: number) => setCurrentSpeed(speed),
      onProgress: () => {},
      onComplete: async (finalResult: SpeedTestResult) => {
        setResults(finalResult); setCurrentSpeed(0); setTestState('finished');
        setLastCompletedMode(testMode);
        
        const newHistoryEntry: Omit<HistoricalTestResult, 'id'> & { id?: number } = {
          ...finalResult,
          date: new Date().toISOString(),
          serverName: selectedServer.name,
          serverLocation: selectedServer.location,
          isp: connectionInfo?.isp || 'Unknown ISP',
          userLocation: connectionInfo?.serverLocation || 'Unknown Location',
          testProtocol: testProtocol,
          connectionMode: connectionMode,
          deviceInfo: parseUserAgent(),
        };

        if (!isAuthenticated) {
            const tempId = Date.now();
            const localHistoryEntry = { ...newHistoryEntry, id: tempId };
            const updatedHistory = [localHistoryEntry, ...history].slice(0, 20);
            setHistory(updatedHistory);
            localStorage.setItem('speedtest-history', JSON.stringify(updatedHistory));
        }
        
        const historyPayload = { ...newHistoryEntry, ip: connectionInfo?.ip || 'Unknown IP' };
        
        try {
            const response = await fetch(`${BASE_URL}/api/history`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(historyPayload),
                credentials: 'include'
            });
            
            if (response.ok) {
                const { id } = await response.json();
                 if (isAuthenticated) {
                    const finalHistoryEntry = { ...newHistoryEntry, id: id || Date.now() };
                    setHistory(prev => [finalHistoryEntry as HistoricalTestResult, ...prev].slice(0, 20));
                }
            }
        } catch(err) {
            console.error("Could not save history to backend:", err);
            // If not authenticated, we've already saved to local storage.
            // If authenticated, we'll just show the new result in the UI for this session.
            const tempId = Date.now();
            setHistory(prev => [{ ...newHistoryEntry, id: tempId } as HistoricalTestResult, ...prev].slice(0, 20));
        }

        setLinkQuality(calculateLinkQuality(finalResult, history)); // use pre-update history for context

        if (testMode === 'speed') {
          fetchRecommendations(finalResult);
        } else {
            setIsAnalyzingStreaming(true);
            setStreamingError(null);
            try {
                const streamingResult = await analyzeStreamingQuality(finalResult);
                setStreamingAnalysis(streamingResult);
            } catch(e) {
                setStreamingError(e instanceof Error ? e.message : "An unknown error occurred.");
            } finally {
                setIsAnalyzingStreaming(false);
            }
        }
        
        if (localStorage.getItem('hasCompletedFirstTest') !== 'true') {
            localStorage.setItem('hasCompletedFirstTest', 'true');
        }
      },
      onError: (message: string) => {
        let finalMessage = message;
        if (!navigator.onLine) { wasTestInterruptedByOffline.current = true; finalMessage = "Your internet connection was lost. The test has been stopped."; }
        setTestState('error'); setTestError(finalMessage); setCurrentSpeed(0); setLinkQuality(getIncompleteTestQuality(finalMessage));
      }
    };
    
    const serverTestUrl = testProtocol === 'websocket' 
      ? BASE_URL // WebSocket service connects to the base URL
      : selectedServer.url;
    
    const testDuration = config?.testConfig?.defaultDuration || 15;

    if (testProtocol === 'websocket') {
        runSpeedTestOverWebSocket(testCallbacks, serverTestUrl, testDuration);
    } else {
        runSpeedTestOverHttp(testCallbacks, serverTestUrl, connectionMode, testDuration);
    }
  };
  
  const addCustomServer = async (url: string) => {
    try {
        const newServer = await validateAndCreateCustomServer(url);
        if (servers.some(s => s.url === newServer.url)) return { success: false, message: 'This server has already been added.' };
        const newServers = [...servers, newServer];
        setServers(newServers); setSelectedServer(newServer); setManualServerSelection(true);
        const customServers = newServers.filter(s => s.id.startsWith('custom_'));
        localStorage.setItem('custom-servers', JSON.stringify(customServers));
        return { success: true, message: 'Server added successfully!' };
    } catch (error) { return { success: false, message: error instanceof Error ? error.message : 'An unknown error occurred.' }; }
  };
  
  const handleSetManualSelection = useCallback(async (manual: boolean) => {
    setManualServerSelection(manual);
    if (manual === false && !isFindingBestServer) {
        setIsFindingBestServer(true);
        const pingPromises = servers.map(server => pingServer(server).then(ping => ({ ...server, ping })));
        const serversWithPing = await Promise.all(pingPromises);
        const sortedServers = [...serversWithPing].sort((a, b) => (a.ping ?? 9999) - (b.ping ?? 9999));
        setServers(sortedServers); setSelectedServer(sortedServers[0]);
        setIsFindingBestServer(false);
    }
  }, [servers, isFindingBestServer]);

  const importServers = async (url: string) => ({ success: false, message: 'Import feature is not yet implemented.' });

  const onAnalyze = async () => {
    if (!connectionInfo || !selectedServer || history.length === 0) return;
    setIsAnalyzing(true); setAIAnalysisModalOpen(true); setAnalysis(null); setAnalysisError(null);
    try {
      const latestTest = history[0];
      const analysisResult = await analyzeSpeedTestResults(
          results,
          history.slice(1, 6),
          connectionInfo,
          selectedServer,
          latestTest.deviceInfo || 'Unknown Device'
      );
      setAnalysis(analysisResult);
    } catch (e) { setAnalysisError(e instanceof Error ? e.message : "An unknown error occurred during analysis."); }
    finally { setIsAnalyzing(false); }
  };

  const handleInstall = () => { if (deferredPrompt) { deferredPrompt.prompt(); setShowInstallPrompt(false); } };
  const handleDismissInstallPrompt = () => { setShowInstallPrompt(false); localStorage.setItem('pwaInstallPromptDismissed', 'true'); };

  const embedCode = `<iframe src="${window.location.origin}" style="width:100%; height:600px; border:0;" title="TeezNet Speed Test"></iframe>`;
  const handleCopyEmbed = () => { if (!embedCopied) { navigator.clipboard.writeText(embedCode).then(() => { setEmbedCopied(true); setTimeout(() => setEmbedCopied(false), 2500); }).catch(err => console.error('Failed to copy embed code: ', err)); } };

  const pageContent = {
    apps: { title: 'Native Apps Coming Soon!', content: <div className="text-center"><p className="mb-4">We are working hard to bring TeezSpeed to all your favorite platforms with dedicated native apps.</p><p>Stay tuned for updates for Android, iOS, Windows, macOS, and Apple TV!</p></div> },
    accessibility: { title: 'Accessibility Statement', content: <div className="space-y-4"><p>TeezSpeed is committed to ensuring digital accessibility for people with disabilities...</p></div> },
    status: { title: 'Network Status', content: <div><div className="flex items-center gap-3 p-4 mb-4 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-lg"><ShieldCheckIcon width={24} height={24} /><span className="font-bold">All Systems Operational</span></div><p className="text-xs text-center text-gray-500 dark:text-slate-400 mt-4">Last checked: {new Date().toLocaleString()}</p></div> },
    privacy: { title: 'Privacy Policy', content: <div className="space-y-4"><p><strong>Last Updated:</strong> {new Date().toLocaleDateString()}</p><p>TeezSpeed is committed to protecting your privacy...</p></div> },
    terms: { title: 'Terms of Use', content: <div className="space-y-4"><p>By accessing or using TeezSpeed, you agree to be bound by these Terms of Use...</p></div> },
    'do-not-sell': { title: 'Do Not Sell My Personal Information', content: <div className="space-y-4"><p>We do not sell your personal information...</p></div> }
  };

  const FooterLink: React.FC<{onClick: () => void, children: React.ReactNode}> = ({ onClick, children }) => ( <li><button onClick={onClick} className="hover:text-cyan-500 dark:hover:text-cyan-400 transition-colors text-left">{children}</button></li> );

  if (configLoading) {
    return (
      <div className="min-h-screen relative flex flex-col items-center justify-center bg-transparent text-white overflow-hidden">
        {/* The background is now handled by the iframe in index.html, so the video and overlay are removed. */}
        <div className="relative z-20 flex flex-col items-center justify-center text-center">
            <img src="/android-chrome-192x192.png" alt="Teez Speed Test Logo" className="w-20 h-20 mb-4" />
            <p className="mt-2 text-xl font-semibold tracking-wider animate-pulse text-slate-800 dark:text-slate-200">Calibrating...</p>
        </div>
      </div>
    );
  }

  if (configError) {
      return <div className="min-h-screen flex items-center justify-center bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-200 p-4">{configError}</div>
  }

  return (
    <div className="bg-transparent text-slate-800 dark:text-slate-200 min-h-screen transition-colors font-sans flex flex-col">
      <header className="sticky top-0 z-30 bg-slate-900/50 backdrop-blur-sm border-b border-white/20 dark:border-slate-700/50">
        <div className="p-4 flex justify-between items-center max-w-7xl mx-auto w-full">
          <a href="/" className="flex items-center gap-3 no-underline hover:opacity-90 transition-opacity" aria-label="Teez Speed Test Home">
            <img src="/favicon-32x32.png" alt="Teez Speed Test Logo" className="w-8 h-8" />
            <h1 className="text-2xl font-bold tracking-tight hidden sm:block text-white [text-shadow:0_1px_2px_rgba(0,0,0,0.5)]">
              <span className="text-cyan-400">Teez</span>{' '}
              <span className="text-purple-400">Speed Test</span>
            </h1>
          </a>
          {/* Desktop Controls */}
          <div className="hidden sm:flex items-center gap-2 sm:gap-4">
              <AuthWidget />
              <div className="w-px h-6 bg-slate-200 dark:bg-slate-700"></div>
              <button onClick={() => setIsSettingsModalOpen(true)} className="p-2 rounded-full text-slate-200 hover:text-white hover:bg-gray-200/20 dark:hover:bg-slate-700/50 transition-colors" aria-label="Open settings" title="Settings"><SettingsIcon /></button>
              <ThemeToggle theme={theme} onToggle={cycleTheme} />
          </div>
          {/* Mobile Menu */}
          <div className="sm:hidden relative" ref={menuRef}>
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="p-2 rounded-full text-slate-200 hover:text-white focus:outline-none" aria-label="Open menu">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7" /></svg>
              </button>
              {isMenuOpen && (
                  <div className="absolute top-full right-0 mt-2 bg-white/80 dark:bg-slate-800/80 backdrop-blur-md border border-white/30 dark:border-slate-700/50 rounded-lg shadow-lg p-4 flex flex-col items-start gap-4 w-56">
                      <AuthWidget />
                      <hr className="w-full border-t border-slate-200 dark:border-slate-700/50"/>
                      <button 
                          onClick={() => { setIsSettingsModalOpen(true); setIsMenuOpen(false); }} 
                          className="w-full flex items-center gap-2 text-left p-2 rounded-md text-slate-700 dark:text-slate-200 hover:bg-gray-200 dark:hover:bg-slate-700/50 transition-colors"
                      >
                          <SettingsIcon />
                          <span>Settings</span>
                      </button>
                      <div className="w-full flex items-center justify-between p-2">
                          <span className="text-slate-700 dark:text-slate-200">Theme</span>
                          <ThemeToggle theme={theme} onToggle={cycleTheme} />
                      </div>
                  </div>
              )}
          </div>
        </div>
      </header>
      <main className="container mx-auto px-4 py-8 flex flex-col items-center gap-12 flex-grow">
        {sharedResult ? ( <ResultPage result={sharedResult} /> ) : (
          <>
            <SpeedTestUI {...{ testState, startTest, currentSpeed, results, connectionInfo, pingHistory, jitterHistory, servers, selectedServer, setSelectedServer, isFindingBestServer, manualServerSelection, setManualServerSelection: handleSetManualSelection, onOpenCustomServerModal, isOnline, connectionMode, setConnectionMode, theme, onAnalyze, isAnalyzing, testError, onOpenFeedbackModal, linkQuality, testMode, setTestMode, streamingAnalysis, isAnalyzingStreaming, streamingError, lastCompletedMode }} />
            <HistoryPanel history={history} onClear={clearHistory} onOpenChart={() => setHistoryChartModalOpen(true)} />
            {testState === 'finished' && testMode === 'speed' && lastCompletedMode === 'speed' && <Recommendations recommendations={recommendations} isLoading={isFetchingRecs} error={recsError} />}
            <SearchKeywords />
          </>
        )}
      </main>
      <footer className="bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm text-sm text-gray-500 dark:text-slate-400 p-8 md:p-12 mt-12">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-y-10 gap-x-8">
            <div>
                <h2 className="font-bold text-base text-slate-700 dark:text-slate-300 mb-2">Embed TeezSpeed</h2>
                <p className="mb-4">Add a free, fast, and modern speed test to your site. Just copy and paste the code below.</p>
                <div className="relative"><pre className="bg-white/20 dark:bg-slate-900/20 backdrop-blur-sm p-4 rounded-lg text-xs overflow-x-auto border border-white/30 dark:border-slate-700/50 text-left"><code className="text-slate-600 dark:text-slate-400">{embedCode}</code></pre><button onClick={handleCopyEmbed} className="absolute top-2 right-2 p-1.5 bg-gray-200 dark:bg-slate-700/80 hover:bg-gray-300 dark:hover:bg-slate-600/80 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-opacity-75" aria-label="Copy embed code" title="Copy embed code">{embedCopied ? <CheckIcon className="w-4 h-4 text-green-500" /> : <ClipboardIcon className="w-4 h-4 text-slate-600 dark:text-slate-300" />}</button></div>
            </div>
            <div className="flex flex-col sm:flex-row gap-8 sm:gap-16">
                <div className="w-full sm:w-1/2"><h2 className="font-bold text-base text-slate-700 dark:text-slate-300 mb-2">Apps</h2><ul className="space-y-2"><FooterLink onClick={() => setInfoModalData(pageContent.apps)}>Android</FooterLink><FooterLink onClick={() => setInfoModalData(pageContent.apps)}>Apple TV</FooterLink><FooterLink onClick={() => setInfoModalData(pageContent.apps)}>iOS</FooterLink><FooterLink onClick={() => setInfoModalData(pageContent.apps)}>macOS</FooterLink><FooterLink onClick={() => setInfoModalData(pageContent.apps)}>Windows</FooterLink></ul></div>
                <div className="w-full sm:w-1/2"><h2 className="font-bold text-base text-slate-700 dark:text-slate-300 mb-2">Company</h2><ul className="space-y-2"><FooterLink onClick={() => setInfoModalData(pageContent.accessibility)}>Accessibility</FooterLink><FooterLink onClick={() => setInfoModalData(pageContent.status)}>Network Status</FooterLink><FooterLink onClick={() => setInfoModalData(pageContent.privacy)}>Privacy Policy</FooterLink><FooterLink onClick={() => setInfoModalData(pageContent.terms)}>Terms of Use</FooterLink><FooterLink onClick={() => setInfoModalData(pageContent['do-not-sell'])}>Do Not Sell My Info</FooterLink></ul></div>
            </div>
        </div>
        <div className="max-w-6xl mx-auto mt-10 pt-8 border-t border-gray-200 dark:border-slate-700/50 text-center sm:text-left"><div className="flex flex-col sm:flex-row justify-between items-center gap-4"><div className="flex flex-wrap justify-center gap-x-4 gap-y-2"><button onClick={() => setPwaModalOpen(true)} className="hover:text-cyan-500 dark:hover:text-cyan-400 transition-colors">How to Install PWA</button><button onClick={() => setRatingsPageOpen(true)} className="hover:text-cyan-500 dark:hover:text-cyan-400 transition-colors">View ISP Ratings</button></div><p>&copy; {new Date().getFullYear()} <a href="https://teztel.com" target="_blank" rel="noopener noreferrer" className="text-cyan-700 dark:text-cyan-400 underline">TeezNet</a>. All Rights Reserved.</p></div></div>
      </footer>
      <HistoryChartModal isOpen={isHistoryChartModalOpen} onClose={() => setHistoryChartModalOpen(false)} history={history} />
      <LinkRestoreModal isOpen={isLinkRestoreModalOpen} onClose={() => { setLinkRestoreModalOpen(false); wasTestInterruptedByOffline.current = false; }} onRestart={() => { setLinkRestoreModalOpen(false); startTest(); }} />
      <FeedbackModal isOpen={isFeedbackModalOpen} onClose={() => setFeedbackModalOpen(false)} connectionInfo={connectionInfo} results={results} latestTestId={history.length > 0 ? history[0].id : null} />
      <CustomServerModal isOpen={isCustomServerModalOpen} onClose={() => setCustomServerModalOpen(false)} onAddServer={addCustomServer} onImportServers={importServers} />
      <AIAnalysisModal isOpen={isAIAnalysisModalOpen} onClose={() => setAIAnalysisModalOpen(false)} analysis={analysis} isLoading={isAnalyzing} error={analysisError} />
      <RatingsPage isOpen={isRatingsPageOpen} onClose={() => setRatingsPageOpen(false)} />
      <PwaInstructionsModal isOpen={isPwaModalOpen} onClose={() => setPwaModalOpen(false)} />
      <SettingsModal 
        isOpen={isSettingsModalOpen} 
        onClose={() => setIsSettingsModalOpen(false)} 
        testProtocol={testProtocol}
        setTestProtocol={(protocol) => {
            setTestProtocol(protocol);
            localStorage.setItem('testProtocol', protocol);
        }}
      />
      <InfoPageModal isOpen={!!infoModalData} onClose={() => setInfoModalData(null)} title={infoModalData?.title || ''} content={infoModalData?.content || null} />
      {showInstallPrompt && platform.current && <PwaInstallPrompt platform={platform.current} onInstall={handleInstall} onClose={handleDismissInstallPrompt} />}
      <PushNotificationPrompt hasCompletedTest={testState === 'finished'}/>
    </div>
  );
};

export default AppContainer;